<template>
  <div class="reader-wrapper">
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading book details...</p>
    </div>

    <div v-else-if="book && (book.name || book.url)" class="reader-container">
      <!-- Header Area -->
      <div class="reader-header">
        <div class="header-title-row">
          <h2>{{ book.name }}</h2>
        </div>
        <div class="action-buttons">
          <button v-if="fetchedBack" class="btn outline" @click="Previous">Previous</button>
          <button class="btn outline" @click="fetchAndShareContent" :disabled="!hasContent">Share</button>
          <button class="btn outline" @click="showLogsModal = true">Logs</button>
          <button class="btn outline" @click="showQueueStatus">Queue Status</button>
          <button v-if="fetchedForward" class="btn primary" @click="Next">Next</button>
          <button v-else-if="hasContent" class="btn primary" @click="Updates">Updates</button>
        </div>
      </div>

      <!-- Logs Modal -->
      <div v-if="showLogsModal" class="modal-overlay" @click.self="showLogsModal = false">
        <div class="modal-content logs-modal">
          <div class="modal-header">
            <h3>Activity Logs</h3>
            <button class="close-btn" @click="showLogsModal = false">&times;</button>
          </div>
          <div class="metrics-summary" v-if="metrics.total > 0">
            <span>Session Hit Rate: <strong>{{ metrics.hitRate }}%</strong> ({{ metrics.cacheHits }}/{{ metrics.total }})</span>
            <span>Avg: <strong>{{ metrics.avgMs }} ms</strong></span>
            <span>L0:{{ metrics.bySource.L0 }} L1:{{ metrics.bySource.L1 }} L2:{{ metrics.bySource.L2 }} API:{{ metrics.bySource.API }}</span>
          </div>
          <div class="logs-list">
            <div v-for="log in logsHistory" :key="log.id" :class="['log-item', log.className]">
              <span class="time">{{ log.time }}</span>
              <span v-html="log.message"></span>
            </div>
            <div v-if="logsHistory.length === 0" class="empty-logs">No activity yet.</div>
          </div>
        </div>
      </div>

      <!-- Toast Notifications -->
      <div class="toast-container">
        <transition-group name="toast">
          <div v-for="toast in toasts" :key="toast.id" :class="['toast', toast.className]">
            <span class="toast-time">{{ toast.time }}</span>
            <span v-html="toast.message"></span>
          </div>
        </transition-group>
      </div>

      <!-- Content Area -->
      <div v-if="hasContent || iFrameLoading" class="content-area">
        <div v-show="iFrameLoading" class="loading-state full-height">
          <div class="spinner"></div>
          <p>Loading chapter...</p>
        </div>
        <div v-show="!iFrameLoading && hasContent" class="iframe-container">
          <iframe
            v-if="fetchedHtml"
            sandbox="allow-same-origin"
            :srcdoc="wrappedHtml"
            @load="onIframeLoad"
            width="100%"
            height="100%"
            frameborder="0"
          ></iframe>
          <iframe
            v-else
            :src="ChapterURL"
            @load="onIframeLoad"
            width="100%"
            height="100%"
            frameborder="0"
          ></iframe>
        </div>
      </div>
      <div v-else class="chapter-error-container">
        <h3>Unable to load chapter</h3>
        <p>There was a problem loading the chapter content.</p>
        <div class="error-actions" style="display: flex; gap: 10px; justify-content: center; margin-top: 15px; flex-wrap: wrap;">
          <button class="btn primary retry-btn" :disabled="isRetrying" @click="retryLoadChapter">
            <span v-if="isRetrying" class="btn-spinner"></span>
            <span>{{ isRetrying ? "Retrying..." : "Retry" }}</span>
          </button>
          <button class="btn outline" @click="showLogsModal = true">View Logs</button>
          <button class="btn outline" @click="showQueueStatus">Queue Status</button>
          <button v-if="fetchedBack" class="btn outline" @click="Previous">Previous Chapter</button>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <h2>Book Not Found</h2>
      <p>The requested book could not be loaded or you do not have permission to view it.</p>
      <router-link to="/ereader" class="btn primary">Back to Library</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import axios from "axios";
import bookStore from "@/stores/book-store";
import userStore from "@/stores/user-store";
import { PRIMARY_API_HOST, FALLBACK_API_HOST, STATIC_HOST } from "@/config/api";
import {
  getChapter,
  peekChapter,
  wrapReaderHtml,
  getSessionMetrics,
} from "@/services/chapterService";
import {
  scheduleChapterPrefetch,
  cancelPrefetch,
} from "@/services/prefetchScheduler";

const route = useRoute();
const uid = computed(() => userStore.getters.getUser?.uid);

const fetchedHtml = ref(null);
const fetchedContent = ref("");
const fetchedBack = ref("");
const fetchedForward = ref("");
const id = ref(route.query.id);
const book = ref({});
const isLoading = ref(true);
const iFrameLoading = ref(true);

const toasts = ref([]);
const logsHistory = ref([]);
const showLogsModal = ref(false);
const metrics = ref(getSessionMetrics());

const hasContent = computed(() =>
  Boolean(fetchedHtml.value || fetchedContent.value)
);
const wrappedHtml = computed(() => wrapReaderHtml(fetchedHtml.value));
const ChapterURL = computed(() => {
  if (!fetchedContent.value) return "";
  return STATIC_HOST + fetchedContent.value;
});

const onIframeLoad = () => {
  iFrameLoading.value = false;
};

const isRetrying = ref(false);

const retryLoadChapter = async () => {
  if (isRetrying.value) return;
  isRetrying.value = true;
  addLog("Retrying chapter load...", "log-info");
  try {
    if (book.value?.url) {
      try {
        await openChapter(book.value.url, { force: false });
      } catch (_cacheErr) {
        addLog("Cached load unavailable, requesting fresh render...", "log-info");
        await openChapter(book.value.url, { force: true });
      }
    }
  } catch (err) {
    addLog(`Retry failed: ${err.message}`, "log-error");
  } finally {
    isRetrying.value = false;
  }
};

const saveBook = () => {
  try {
    bookStore.dispatch("updateBook", { id: id.value, book: book.value });
  } catch (error) {
    console.error("Could not updateBook " + error);
  }
};

const updateLastRead = () => {
  book.value.timestamp = new Date().toISOString();
  saveBook();
};

const applyChapterData = async (chapter) => {
  fetchedHtml.value = chapter.html || null;
  fetchedContent.value = chapter.htmlURL || "";
  const bck = chapter.backAbsUrl || chapter.backUrl || "";
  let fwd = chapter.forwardAbsUrl || chapter.forwardUrl || "";
  if (fwd && bck && fwd === bck) {
    fwd = "";
  }
  fetchedBack.value = bck;
  fetchedForward.value = fwd;
  metrics.value = getSessionMetrics();

  if (!fetchedHtml.value && fetchedContent.value) {
    try {
      const resp = await axios.get(ChapterURL.value, { timeout: 10000 });
      if (resp && resp.data && typeof resp.data === "string") {
        fetchedHtml.value = resp.data;
      }
    } catch {
      // Fall back to direct iframe src
    }
  }

  iFrameLoading.value = false;
  updateLastRead();
  scheduleChapterPrefetch(chapter, book.value, addLog);
};

const openChapter = async (targetUrl = book.value.url, { force = false } = {}) => {
  if (!targetUrl) return;

  // Task 3.4: Instant swap from L0 memory cache without showing spinner
  if (!force) {
    const cached = peekChapter(targetUrl);
    if (cached && cached.html) {
      applyChapterData(cached);
      addLog(`Instant load from ${cached.source} (${cached.ms || 0}ms)`, "log-success");
      return;
    }
  }

  iFrameLoading.value = true;
  try {
    const chapter = await getChapter(targetUrl, {
      priority: "user",
      book: book.value,
      force,
    });
    applyChapterData(chapter);
    addLog(
      `Loaded from ${chapter.source} in ${chapter.ms || 0}ms`,
      "log-success"
    );
  } catch (error) {
    iFrameLoading.value = false;
    addLog(`Error loading chapter: ${error.message}`, "log-error");
    throw error;
  }
};

const fetchBookData = async () => {
  isLoading.value = true;
  try {
    let foundBook = bookStore.getters.getBook(id.value);

    if (!foundBook && uid.value) {
      await bookStore.dispatch("fetchBooks", uid.value);
      foundBook = bookStore.getters.getBook(id.value);
    }

    if (!foundBook && id.value) {
      foundBook = await bookStore.dispatch("fetchBookById", id.value);
    }

    book.value = foundBook || {};
  } catch (error) {
    console.error("Error fetching books or book data:", error);
  } finally {
    isLoading.value = false;
  }

  if (book.value?.url) {
    openChapter(book.value.url);
  }
};

watch(uid, (newUid) => {
  if (newUid && (!book.value || !book.value.url)) {
    fetchBookData();
  }
});

const Next = () => {
  if (!fetchedForward.value) return;
  const targetUrl = fetchedForward.value;
  book.value.url = targetUrl;
  logsHistory.value = [];
  addLog("Getting next..", "log-info");
  openChapter(targetUrl);
  saveBook();
};

const Previous = () => {
  if (!fetchedBack.value) return;
  const targetUrl = fetchedBack.value;
  book.value.url = targetUrl;
  logsHistory.value = [];
  addLog("Getting previous..", "log-info");
  openChapter(targetUrl);
  saveBook();
};

const Updates = async () => {
  logsHistory.value = [];
  addLog("Checking for any changes...", "log-info");
  await openChapter(book.value.url, { force: true });
  if (fetchedForward.value) {
    addLog("New chapter found! Click Next to read.", "log-success");
  } else {
    addLog("No new chapters available yet.", "log-info");
  }
};

const addLog = (message, className) => {
  const logId = Date.now() + Math.random();
  const time = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const logItem = { id: logId, message, className, time };
  toasts.value.push(logItem);
  logsHistory.value.unshift(logItem);
  setTimeout(() => {
    toasts.value = toasts.value.filter((t) => t.id !== logId);
  }, 4000);
};

const fetchAndShareContent = async () => {
  try {
    const htmlToShare = fetchedHtml.value;
    if (!htmlToShare) {
      alert("No chapter content loaded yet to share.");
      return;
    }

    // Extract clean plain text from the rendered HTML for TTS / e-readers like Evie
    const doc = new DOMParser().parseFromString(htmlToShare, "text/html");
    const plainText = (doc.body.innerText || doc.body.textContent || "").trim();

    if (!plainText) {
      alert("No readable text found to share.");
      return;
    }

    if (navigator.share) {
      await navigator.share({
        title: book.value?.name || "Chapter",
        text: plainText,
      });
    } else {
      alert("Web Share API is not supported in this browser.");
    }
  } catch (error) {
    if (error.name !== "AbortError") {
      console.error("Error sharing content:", error);
    }
  }
};

const showQueueStatus = async () => {
  try {
    let response;
    let label = "Local";
    try {
      response = await axios.get(`${PRIMARY_API_HOST}/queue-status`, { timeout: 2500 });
    } catch (_err) {
      if (PRIMARY_API_HOST !== FALLBACK_API_HOST) {
        label = "Cloud Run";
        response = await axios.get(`${FALLBACK_API_HOST}/queue-status`, { timeout: 5000 });
      } else {
        throw _err;
      }
    }
    const q = response.data.queue;
    addLog(
      `Queue [${label}]: Active=${q.activeRequests}/${q.maxConcurrent}, Queued=${q.queuedRequests}/${q.maxQueueSize}, Processed=${q.totalProcessed}`,
      "log-info"
    );
  } catch (error) {
    addLog(`Error fetching queue status: ${error.message}`, "log-error");
  }
};

const backgroundSuccessListener = () => {
  addLog("✅ Background request completed!", "log-success");
  openChapter(book.value?.url);
};

const backgroundFailedListener = () => {
  addLog("❌ Request failed after retries", "log-error");
};

onMounted(() => {
  window.addEventListener("backgroundRequestSuccess", backgroundSuccessListener);
  window.addEventListener("backgroundRequestFailed", backgroundFailedListener);
  fetchBookData();
});

onUnmounted(() => {
  cancelPrefetch();
  window.removeEventListener("backgroundRequestSuccess", backgroundSuccessListener);
  window.removeEventListener("backgroundRequestFailed", backgroundFailedListener);
});
</script>

<style scoped lang="scss">
.reader-wrapper {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.reader-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
}

.reader-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  .header-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .api-status-pill {
    font-size: 0.8rem;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(241, 196, 15, 0.18);
    border: 1px solid rgba(241, 196, 15, 0.45);
    color: #f1c40f;
  }

  h2 {
    color: $text-color;
    font-size: 1.5rem;
    margin: 0;
    word-break: break-word;
  }
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;

  .btn {
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;

    &.primary {
      background: rgba(6, 182, 212, 0.15);
      border: 1px solid rgba(6, 182, 212, 0.4);
      color: $cyan;
      &:hover {
        background: rgba(6, 182, 212, 0.25);
        box-shadow: 0 0 12px rgba(6, 182, 212, 0.3);
        transform: translateY(-2px);
      }
    }
    
    &.outline {
      background: transparent;
      color: $text-color;
      border: 1px solid rgba(0,0,0,0.2);
      &:hover {
        background: rgba(0,0,0,0.05);
      }
    }
  }
}

.content-area {
  flex: 1;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
}

.iframe-container {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  position: relative;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;

  iframe {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
}

@media (max-width: 768px) {
  .reader-wrapper {
    padding: 8px 0;
    height: calc(100vh - 120px);
    height: calc(100dvh - 120px);
    min-height: 480px;
  }

  .reader-header {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 12px;

    .header-title-row {
      justify-content: center;
      width: 100%;
    }

    h2 {
      text-align: center;
      font-size: 1.25rem;
    }
  }

  .action-buttons {
    justify-content: center;
    width: 100%;
    gap: 6px;

    .btn {
      padding: 6px 12px;
      font-size: 0.85rem;
    }
  }

  .content-area {
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $text-color;

  &.full-height {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.9);
    z-index: 10;
    color: #333;
    
    .spinner {
      border-top-color: $cyan;
      border-color: rgba(255,255,255,0.1) rgba(255,255,255,0.1) rgba(255,255,255,0.1) $cyan;
    }
  }

  .spinner {
    border: 4px solid rgba(255, 255, 255, 0.1);
    border-top: 4px solid $cyan;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin-bottom: 15px;
  }
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

.toast {
  background: rgba(40, 40, 40, 0.95);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  backdrop-filter: blur(5px);
  border-left: 4px solid #aaa;
  font-size: 0.9rem;
  
  &.log-info { border-left-color: #3498db; }
  &.log-success { border-left-color: #2ecc71; }
  &.log-error { border-left-color: #e74c3c; }
  &.log-warning { border-left-color: #f1c40f; }

  .toast-time {
    display: block;
    font-size: 0.75rem;
    opacity: 0.7;
    margin-bottom: 3px;
  }
}

/* Toast Transitions */
.toast-enter-active, .toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Logs Modal */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content.logs-modal {
  background: rgba(30, 30, 30, 0.95);
  border: 1px solid rgba(255,255,255,0.1);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 15px;
  margin-bottom: 15px;
  
  h3 {
    margin: 0;
    color: white;
  }
  
  .close-btn {
    background: transparent;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
    &:hover { color: $cyan; text-shadow: 0 0 8px rgba(6, 182, 212, 0.5); }
  }
}

.metrics-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: rgba(6, 182, 212, 0.12);
  border: 1px solid rgba(6, 182, 212, 0.3);
  color: #e2e8f0;
  font-size: 0.8rem;
}

.logs-list {
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.log-item {
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-radius: 8px;
  background: rgba(255,255,255,0.05);
  color: #ddd;
  font-size: 0.9rem;
  border-left: 3px solid #aaa;
  
  &.log-info { border-left-color: #3498db; }
  &.log-success { border-left-color: #2ecc71; }
  &.log-error { border-left-color: #e74c3c; }
  &.log-warning { border-left-color: #f1c40f; }

  .time {
    font-size: 0.75rem;
    color: #888;
    margin-bottom: 4px;
  }
}

.empty-logs {
  text-align: center;
  color: #888;
  padding: 20px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
  text-align: center;
  gap: 12px;
  color: $text-color;

  h2 {
    font-size: 1.5rem;
    margin: 0;
  }

  p {
    color: #888;
    margin: 0;
  }

  .btn {
    margin-top: 12px;
    text-decoration: none;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    background: rgba(6, 182, 212, 0.15);
    border: 1px solid rgba(6, 182, 212, 0.4);
    color: $cyan;
    display: inline-block;
  }
}

.chapter-error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
  padding: 40px 20px;
  text-align: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;

  h3 {
    margin: 0;
    color: $text-color;
  }

  p {
    margin: 0;
    color: #888;
  }

  .btn {
    margin-top: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    background: rgba(6, 182, 212, 0.15);
    border: 1px solid rgba(6, 182, 212, 0.4);
    color: $cyan;
  }
}
</style>
